import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import {
  Form,
  Hyperlink,
  StatefulButton
} from '@openedx/paragon';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { forgotPassword, setForgotPasswordFormData } from './data/actions';
import { forgotPasswordResultSelector } from './data/selectors';
import ForgotPasswordAlert from './ForgotPasswordAlert';
import { FormGroup } from '../common-components';
import { DEFAULT_STATE, LOGIN_PAGE, VALID_EMAIL_REGEX } from '../data/constants';
import { updatePathWithQueryParams, windowScrollTo } from '../data/utils';

import authBG from '../assets/authBG.jpg';
import logo from '../assets/logo.png';
/* eslint-disable-next-line global-require */
const arMessages = require("../translations/ar.js");
import { useT } from '../context/LanguageContext';

const ForgotPasswordPage = (props) => {
  const platformName = getConfig().SITE_NAME;
  const emailRegex = new RegExp(VALID_EMAIL_REGEX, 'i');
  const {
    status, submitState, emailValidationError } = props;


  const { t, toggleLang, isArabic } = useT();
  const [email, setEmail] = useState(props.email);
  const [bannerEmail, setBannerEmail] = useState('');
  const [formErrors, setFormErrors] = useState('');
  const [validationError, setValidationError] = useState(emailValidationError);


  useEffect(() => {
    sendPageEvent('login_and_registration', 'reset');
    sendTrackEvent('edx.bi.password_reset_form.viewed', { category: 'user-engagement' });
  }, []);

  useEffect(() => {
    setValidationError(emailValidationError);
  }, [emailValidationError]);

  useEffect(() => {
    if (status === 'complete') {
      setEmail('');
    }
  }, [status]);

  const getValidationMessage = (value) => {
    let error = '';

    if (value === '') {
      error = t('forgot.password.empty.email.field.error');
    } else if (!emailRegex.test(value)) {
      error = t('forgot.password.page.invalid.email.message');
    }

    return error;
  };

  const handleBlur = () => {
    props.setForgotPasswordFormData({ email, emailValidationError: getValidationMessage(email) });
  };

  const handleFocus = () => props.setForgotPasswordFormData({ emailValidationError: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    setBannerEmail(email);

    const error = getValidationMessage(email);
    if (error) {
      setFormErrors(error);
      props.setForgotPasswordFormData({ email, emailValidationError: error });
      windowScrollTo({ left: 0, top: 0, behavior: 'smooth' });
    } else {
      props.forgotPassword(email);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('forgot.password.page.title', { siteName: getConfig().SITE_NAME })}
        </title>
      </Helmet>

      <div
        className="ksu-login-page"
        style={{
          backgroundImage: `url(${authBG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Language Switcher */}
        <div className="ksu-lang-switcher">
          <button type="button" className="lang-btn" onClick={toggleLang}>
            {isArabic ? 'EN' : 'العربية'}
          </button>
        </div>

        {/* Header with Logo */}
        <header className="ksu-header">
          <div className="ksu-logo-container">
            <a href="/">
              <img
                src={logo}
                alt="Platform Logo"
                className="ksu-logo"
              />
            </a>
          </div>
        </header>

        {/* Main Content Card */}
        <main className="ksu-main-content">
          <div className="ksu-login-card">
            <h1 className="ksu-login-title">
              {t('forgot.password.page.heading')}
            </h1>

            <div className="ksu-register-link">
              <Link to={updatePathWithQueryParams(LOGIN_PAGE)}>
                {t('sign.in.text')}
              </Link>
            </div>

            <Form id="forget-password-form" name="forget-password-form" className="ksu-form">
              <ForgotPasswordAlert email={bannerEmail} emailError={formErrors} status={status} />

              <p className="mb-4" style={{ textAlign: 'start' }}>
                {t('forgot.password.page.instructions')}
              </p>

              <div className="ksu-form-group">
                <FormGroup
                  floatingLabel={t('forgot.password.page.email.field.label')}
                  name="email"
                  value={email}
                  autoComplete="on"
                  errorMessage={validationError}
                  handleChange={(e) => setEmail(e.target.value)}
                  handleBlur={handleBlur}
                  handleFocus={handleFocus}
                  helpText={[t('forgot.password.email.help.text', { platformName })]}
                />
              </div>

              <StatefulButton
                id="submit-forget-password"
                name="submit-forget-password"
                type="submit"
                variant="brand"
                className="ksu-login-btn"
                state={submitState}
                labels={{
                  default: t('forgot.password.page.submit.button'),
                  pending: ''
                }}
                onClick={handleSubmit}
                onMouseDown={(e) => e.preventDefault()}
              />

              {(getConfig().LOGIN_ISSUE_SUPPORT_LINK) && (
                <div className="mt-3" style={{ textAlign: 'start' }}>
                  <Hyperlink
                    id="forgot-password"
                    name="forgot-password"
                    className="font-weight-500 text-body"
                    destination={getConfig().LOGIN_ISSUE_SUPPORT_LINK}
                    target="_blank"
                    showLaunchIcon={false}
                  >
                    {t('need.help.sign.in.text')}
                  </Hyperlink>
                </div>
              )}

              <p className="mt-4 small text-gray-700" style={{ textAlign: 'start' }}>
                {t('additional.help.text', { platformName })}
                <span>
                  <Hyperlink isInline destination={`mailto:${getConfig().INFO_EMAIL}`}>{getConfig().INFO_EMAIL}</Hyperlink>
                </span>
              </p>
            </Form>
          </div>
        </main>
      </div>
    </>
  );
};

ForgotPasswordPage.propTypes = {
  email: PropTypes.string,
  emailValidationError: PropTypes.string,
  forgotPassword: PropTypes.func.isRequired,
  setForgotPasswordFormData: PropTypes.func.isRequired,
  status: PropTypes.string,
  submitState: PropTypes.string,
  isArabic: PropTypes.bool.isRequired,
};

ForgotPasswordPage.defaultProps = {
  email: '',
  emailValidationError: '',
  status: null,
  submitState: DEFAULT_STATE
};

const ConnectedForgotPasswordPage = connect(
  forgotPasswordResultSelector,
  {
    forgotPassword,
    setForgotPasswordFormData
  },
)(ForgotPasswordPage);

// Wrapper component with IntlProvider for Arabic locale
const ForgotPasswordPageWrapper = (props) => {

  if (props.isArabic) {
    return (
      <IntlProvider locale="ar" messages={arMessages}>
        <ConnectedForgotPasswordPage {...props} />
      </IntlProvider>
    );
  }

  return <ConnectedForgotPasswordPage {...props} />;
};

export default ForgotPasswordPageWrapper;
